import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {useSnackbar} from "notistack";
import {ColorPicker, useColor} from "react-color-palette";
import React from "react";
import {makeRequest} from "../../../services/requestService";
import {API_URL} from "../../../constants";
import PropTypes from "prop-types";

export default function TagCreatorComponent(props) {
    const {collectionId, onSubmit} = props;
    const {enqueueSnackbar} = useSnackbar();
    const [state, setState] = React.useState({
        collectionId: collectionId,
        color: "#9cc5c8",
        description: "",
        label: "",
        type: "",
        altoType: "NAMED_ENTITY",
        uri: ""
    });

    const handleSubmit = () => {
        makeRequest(`${API_URL}/label`, "POST", state).then((response) => {
            if (response.status === 201) {
                enqueueSnackbar(response.data.message, {variant: "success"});
                onSubmit();
            }
        });
    }

    return (<TagEditorComponent state={state} onStateUpdate={setState} onSubmit={handleSubmit}/>)
}

TagCreatorComponent.propTypes = {
    collectionId: PropTypes.number.isRequired,
    onSubmit: PropTypes.func.isRequired
}

export function TagUpdaterComponent(props) {
    const {label, onSubmit} = props;
    const {enqueueSnackbar} = useSnackbar();
    const [state, setState] = React.useState({
        collectionId: label.collection,
        color: label.color,
        description: label.description,
        label: label.label,
        altoType: label.altoType,
        type: label.type,
        uri: label.uri
    });

    const handleSubmit = () => {
        makeRequest(`${API_URL}/label/${label.id}`, "PUT", state).then((response) => {
            if (response.status === 200) {
                enqueueSnackbar(response.data.message, {variant: "success"});
                onSubmit();
            }
        });
    }

    return (<TagEditorComponent state={state} onStateUpdate={setState} onSubmit={handleSubmit}/>)
}

export function TagEditorComponent(props) {
    const {state, onStateUpdate, onSubmit} = props;
    const {t} = useTranslation('common');
    const theme = useTheme();
    const [color, setColor] = useColor("hex", state.color ? state.color : "#9cc5c8");
    const sm = useMediaQuery(theme.breakpoints.down("sm"));
    const typeValues = ["ROLE", "NAMED_ENTITY", "OTHER_TAG", "STRUCTURE", "LAYOUT"];

    React.useEffect(() => {
        let newState = {...state};
        newState.color = color.hex;
        onStateUpdate(newState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [color]);

    const handleLabelChange = (event) => {
        let newState = {...state};
        newState.label = event.target.value;
        onStateUpdate(newState);
    }

    const handleDescriptionChange = (event) => {
        let newState = {...state};
        newState.description = event.target.value;
        onStateUpdate(newState);
    }

    const handleURIChange = (event) => {
        let newState = {...state};
        newState.uri = event.target.value;
        onStateUpdate(newState);
    }

    const handleAltoTypeChange = (event) => {
        let newState = {...state};
        newState.altoType = event.target.value;
        onStateUpdate(newState);
    }

    const handleTypeChange = (event) => {
        let newState = {...state};
        newState.type = event.target.value;
        onStateUpdate(newState);
    }

    return (
        <Box m={1}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        color="secondary"
                        placeholder={t("main.pages.profile.annotation.card.creation.label")}
                        variant="standard"
                        value={state.label}
                        type="text"
                        required
                        onChange={handleLabelChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        color="secondary"
                        placeholder="Description"
                        label="Description"
                        size="small"
                        value={state.description}
                        type="text"
                        onChange={handleDescriptionChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        color="secondary"
                        placeholder="URI"
                        label="URI"
                        size="small"
                        value={state.uri}
                        type="url"
                        onChange={handleURIChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        color="secondary"
                        placeholder="Type"
                        label="Type"
                        size="small"
                        value={state.type}
                        type="text"
                        onChange={handleTypeChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel id="type-select-label">AltoType</InputLabel>
                        <Select
                            labelId="type-select-label"
                            id="type-select"
                            value={state.altoType}
                            label="AltoType"
                            color="secondary"
                            size="small"
                            onChange={handleAltoTypeChange}
                        >
                            {typeValues.map((value) => (
                                <MenuItem key={value} value={value}>{value}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <Stack direction="row" spacing={1} width="100%" alignItems="center">
                        <Typography variant="caption">
                            {t("main.pages.profile.annotation.card.creation.color")}
                        </Typography>
                        <Divider sx={{flexGrow: 1}}/>
                    </Stack>
                </Grid>
                <Grid item xs={12}>
                    <Box display="flex" direction="column" alignItems="center" justifyContent="center">
                        <ColorPicker
                            width={sm ? 200 : 400}
                            height={180}
                            color={color}
                            alpha={false}
                            onChange={setColor}
                            hideHSV
                            hideRGB
                            dark={theme.palette.mode === "dark"}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={onSubmit}
                    >
                        {t('main.components.saveDialog.save')}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
}

TagEditorComponent.propTypes = {
    state: PropTypes.shape({
        collectionId: PropTypes.number,
        color: PropTypes.string,
        description: PropTypes.string,
        label: PropTypes.string,
        altoType: PropTypes.string,
        type: PropTypes.string,
        uri: PropTypes.string
    }).isRequired,
    onStateUpdate: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
}